import React, { useEffect, useState } from "react";
import { UploadFileBody, UploadFileContainer } from "./FileUploader.styled";
import {
  MB_LABEL,
  UPLOAD_FILE_1_LABEL,
  UPLOAD_FILE_2_LABEL,
  UPLOAD_FILE_ERROR_DIMENSION_LABEL,
  UPLOAD_FILE_ERROR_TYPE_LABEL,
  UPLOAD_FILE_SUBTITLE_LABEL,
} from "../../../constants/Dictionary";
import { BodyTyp } from "../../Typography/Typography";
import {
  FileUploaderSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
} from "../../../types/AppTypes";
import { theme } from "../../../constants/StyledComponents";
import { Link, LinkSizeEnum } from "../Link/Link";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { IconManager } from "../IconManager/IconManager";
import { ReactComponent as CloseIcon } from "../../../icons/close.svg";
import { ReactComponent as BinIcon } from "../../../icons/bin.svg";
import { ReactComponent as EyeIcon } from "../../../icons/eye.svg";
import { ReactComponent as FileAttachementIcon } from "../../../icons/fileAttachment.svg";
import addFile from "../../../images/addFile.svg";
import { Loader } from "../Loader/Loader";

type FileUploaderProps = {
  size: FileUploaderSizeEnum;
  onFileUpload?: (file: File) => void;
  fileName?: string;
  fileSize?: number;
  handleDeleteFile?: () => void;
  handleViewFile?: () => void;
  showIconEye?: boolean;
  value: File | null;
};

export const FileUploader = ({
  size,
  onFileUpload,
  fileName,
  fileSize,
  handleDeleteFile,
  handleViewFile,
  value,
  showIconEye = false,
}: FileUploaderProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [showProgressBar, setShowProgressBar] = useState(false);

  const maxFileSizeMB = 5;
  const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

  const handleFileSelect = (file: File) => {
    const allowedExtensions = ["pdf"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();

    if (file.size > maxFileSizeBytes) {
      setError(UPLOAD_FILE_ERROR_DIMENSION_LABEL);
      return;
    }

    if (!allowedExtensions.includes(fileExtension ?? "")) {
      setError(UPLOAD_FILE_ERROR_TYPE_LABEL);
      return;
    }
    setError(null);
    uploadFile(file);
  };

  const uploadFile = (file: File) => {
    setShowProgressBar(true);
    const fakeUpload = setInterval(() => {
      setUploadProgress((prevProgress) => {
        if (prevProgress === 100) {
          if (onFileUpload) {
            onFileUpload(file);
          }
          clearInterval(fakeUpload);
          return 100;
        }
        return prevProgress + 10;
      });
    }, 100);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const renderIcons = () => {
    return (
      <div className="container-icon">
        {showIconEye && (
          <IconManager
            marginRight={10}
            size={IconSizeEnum.MEDIUM}
            onClick={handleViewFile}
          >
            <EyeIcon />
          </IconManager>
        )}
        <IconManager size={IconSizeEnum.MEDIUM} onClick={handleDeleteFile}>
          <BinIcon />
        </IconManager>
      </div>
    );
  };

  useEffect(() => {
    if (uploadProgress === 100 && value) {
      onFileUpload && onFileUpload(value);
      setShowProgressBar(false);
    }
  }, [value, uploadProgress]);

  return (
    <UploadFileContainer $selectedFile={!!(value || fileName)}>
      {value || fileName ? (
        <UploadFileBody>
          <div className="container-details-file">
            <IconManager size={IconSizeEnum.MEDIUM}>
              <FileAttachementIcon />
            </IconManager>
            <div>
              <BodyTyp
                variant="1"
                fontWeight={FontWeightEnum.SEMIBOLD}
                color={theme.colors.black}
              >
                {fileName ?? value?.name}
              </BodyTyp>
              <BodyTyp
                variant="3"
                fontWeight={FontWeightEnum.REGULAR}
                color={theme.colors.black}
              >
                {value ? (
                  `${(value.size / (1024 * 1024)).toFixed(2)} ${MB_LABEL}`
                ) : fileName && fileSize ? (
                  `${(fileSize / (1024 * 1024)).toFixed(2)} ${MB_LABEL}`
                ) : (
                  <Loader size={18} loading={true} />
                )}
              </BodyTyp>
              {showProgressBar && <ProgressBar progress={uploadProgress} />}
            </div>
          </div>
          <IconManager size={IconSizeEnum.MEDIUM} onClick={handleDeleteFile}>
            {uploadProgress === 100 || fileName ? renderIcons() : <CloseIcon />}
          </IconManager>
        </UploadFileBody>
      ) : (
        <UploadFileBody
          $isDragOver={isDragOver}
          $size={size}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <img src={addFile} alt="addFile" />
          <div>
            <BodyTyp
              variant="1"
              fontWeight={FontWeightEnum.MEDIUM}
              color={theme.colors.black}
            >
              <Link
                onClick={() => document.getElementById("fileInput")?.click()}
                size={LinkSizeEnum.REGULAR}
                underline={true}
              >
                {UPLOAD_FILE_1_LABEL}
              </Link>
              <span className="upload-label">{UPLOAD_FILE_2_LABEL}</span>
            </BodyTyp>
            <BodyTyp
              variant="3"
              fontWeight={FontWeightEnum.REGULAR}
              color={theme.colors.black}
            >
              {UPLOAD_FILE_SUBTITLE_LABEL}
            </BodyTyp>
            <input
              id="fileInput"
              type="file"
              style={{ display: "none" }}
              onChange={handleInputChange}
            />
            {error && (
              <BodyTyp
                variant="3"
                fontWeight={FontWeightEnum.SEMIBOLD}
                color={theme.colors.red_1}
              >
                {error}
              </BodyTyp>
            )}
          </div>
        </UploadFileBody>
      )}
    </UploadFileContainer>
  );
};
